import {useState} from 'react'
import {TrashIcon} from '@heroicons/react/24/outline'
import FormModal from "../general/FormModal.tsx";
import Popup from "../general/Popup.tsx";
import {AddressFields, type AddressFieldValue} from "../general/AddressFields.tsx";
import {fieldLabelClasses, fieldVariants} from "../general/fieldStyles.ts";
import {cn} from "../../lib/utils.ts";
import {Customer, CustomerAddress, CustomerDraft} from "../../api/customers/Customer.ts";

const EMPTY_ADDRESS_FIELD: AddressFieldValue = {
    street_name: '',
    house_number: '',
    city_name: '',
    postal_code: '',
    country: 'NL',
}

function toAddressFieldValue(address: CustomerAddress): AddressFieldValue {
    return {
        street_name: address.street,
        house_number: address.house_number,
        city_name: address.city,
        postal_code: address.postal_code,
        country: address.country || 'NL',
    }
}

function toCustomerAddress(value: AddressFieldValue): CustomerAddress {
    return {
        street: value.street_name.trim(),
        house_number: value.house_number.trim(),
        postal_code: value.postal_code.trim(),
        city: value.city_name.trim(),
        country: value.country.trim() || 'NL',
    }
}

function isAddressComplete(address: CustomerAddress): boolean {
    return Boolean(address.street && address.house_number && address.postal_code && address.city)
}

type FormErrors = Partial<Record<'address', string>>

interface CustomerFormModalProps {
    open: boolean
    sessionKey: number
    customer: Customer | null
    isSaving: boolean
    onClose: () => void
    onSave: (draft: CustomerDraft) => void
    onArchive: (customer: Customer) => void
    initialCompanyName?: string
}

export default function CustomerFormModal(props: CustomerFormModalProps) {
    return <CustomerFormModalInner key={props.sessionKey} {...props}/>
}

function CustomerFormModalInner({
    open, customer, isSaving, onClose, onSave, onArchive, initialCompanyName = '',
}: CustomerFormModalProps) {

    const [companyName, setCompanyName] = useState(customer?.company_name ?? initialCompanyName)
    const [addressExtraLine, setAddressExtraLine] = useState(customer?.address_extra_line ?? '')
    const [address, setAddress] = useState<AddressFieldValue>(
        customer ? toAddressFieldValue(customer.address) : EMPTY_ADDRESS_FIELD,
    )
    const [email, setEmail] = useState(customer?.email ?? '')
    const [fixedCcEmail, setFixedCcEmail] = useState(customer?.fixed_cc_email ?? '')
    const [phoneNumber, setPhoneNumber] = useState(customer?.phone_number ?? '')
    const [vatNumber, setVatNumber] = useState(customer?.vat_number ?? '')
    const [errors, setErrors] = useState<FormErrors>({})
    const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)

    const isEditing = customer !== null

    function handleSave() {
        const customerAddress = toCustomerAddress(address)

        if (!isAddressComplete(customerAddress)) {
            setErrors({address: 'Vul straatnaam, huisnummer, postcode en plaats in.'})
            return
        }
        setErrors({})

        const draft: CustomerDraft = {
            company_name: companyName.trim(),
            address_extra_line: addressExtraLine.trim() || null,
            address: customerAddress,
            email: email.trim() || null,
            fixed_cc_email: fixedCcEmail.trim() || null,
            phone_number: phoneNumber.trim() || null,
            vat_number: vatNumber.trim() || null,
            default_payment_term_days: customer?.default_payment_term_days ?? null,
        }
        onSave(draft)
    }

    return (
        <FormModal
            open={open}
            onClose={onClose}
            onSubmit={handleSave}
            title={isEditing ? 'Klant bewerken' : 'Nieuwe klant'}
            subtitle="Deze gegevens komen automatisch op elke factuur voor deze klant."
            isSubmitting={isSaving}
            leftFooterContent={isEditing ? (
                <button
                    type="button"
                    onClick={() => setIsArchiveConfirmOpen(true)}
                    className="flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-500"
                >
                    <TrashIcon className="h-4 w-4"/>
                    Verwijderen
                </button>
            ) : undefined}
        >
            <div className="flex flex-col gap-5">
                <div>
                    <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-company-name">Bedrijfsnaam *</label>
                    <input
                        id="customer-company-name"
                        required
                        className={cn(fieldVariants())}
                        value={companyName}
                        placeholder="Bijv. Acme Corp B.V."
                        onChange={(e) => setCompanyName(e.target.value)}
                    />
                </div>

                <div>
                    <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-extra-line">Extra adresregel</label>
                    <input
                        id="customer-extra-line"
                        className={cn(fieldVariants())}
                        value={addressExtraLine}
                        placeholder="Bijv. T.a.v. afd. Inkoop · Gebouw Noord, 4e verdieping"
                        onChange={(e) => setAddressExtraLine(e.target.value)}
                    />
                </div>

                <div>
                    <label className={cn(fieldLabelClasses, "mb-1.5")}>Adres *</label>
                    <AddressFields
                        value={address}
                        onChange={(value) => {
                            setAddress(value)
                            setErrors(prev => ({...prev, address: undefined}))
                        }}
                        idPrefix="customer-address"
                    />
                    {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                        <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-email">E-mail</label>
                        <input
                            id="customer-email"
                            type="email"
                            className={cn(fieldVariants())}
                            value={email}
                            placeholder="finance@klant.nl"
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-cc-email">CC (vast)</label>
                        <input
                            id="customer-cc-email"
                            type="email"
                            className={cn(fieldVariants())}
                            value={fixedCcEmail}
                            placeholder="crediteuren@klant.nl"
                            onChange={(e) => setFixedCcEmail(e.target.value)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                        <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-phone">Telefoon</label>
                        <input
                            id="customer-phone"
                            type="tel"
                            className={cn(fieldVariants())}
                            value={phoneNumber}
                            placeholder="020 123 45 67"
                            onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className={cn(fieldLabelClasses, "mb-1.5")} htmlFor="customer-vat-number">Btw-nummer</label>
                        <input
                            id="customer-vat-number"
                            className={cn(fieldVariants())}
                            value={vatNumber}
                            placeholder="NL001234567B01"
                            onChange={(e) => setVatNumber(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {customer && (
                <Popup
                    open={isArchiveConfirmOpen}
                    onClose={() => setIsArchiveConfirmOpen(false)}
                    onConfirm={() => {
                        setIsArchiveConfirmOpen(false)
                        onArchive(customer)
                    }}
                    title="Klant verwijderen"
                    variant="danger"
                    confirmButtonDisplayLabel="Verwijderen"
                >
                    <p className="text-sm text-gray-700">
                        Weet je zeker dat je <span className="font-semibold">{customer.company_name}</span> wilt
                        verwijderen? Bestaande facturen blijven bewaard.
                    </p>
                </Popup>
            )}
        </FormModal>
    )
}
